import type {
  Duration,
  ExecutionContext,
  HumanTaskActor,
  HumanTaskHandle,
  HumanTaskSubject,
  NodeExecutionContext,
  ResumeContext,
} from "../contracts/runtimeTypes";
import type { Item, JsonValue, NodeInspectorSummaryRow, NodeConfigBase } from "../contracts/workflowTypes";
import type { CredentialJsonRecord } from "../contracts/credentialTypes";
import type { ZodObject, ZodType } from "zod";
import type { DefinedNodeCredentialBindings } from "./defineNode.types";
import { SuspensionRequest } from "../contracts/runtimeTypes";
import { defineNode } from "./defineNode.types";
import type { DefinedNode } from "./defineNode.types";

export interface HumanApprovalDecisionResult {
  readonly status: "approved" | "rejected" | "timed-out" | "auto-accepted";
  readonly actor?: HumanTaskActor;
  readonly decidedAt?: Date;
  readonly note?: string;
  readonly payload?: Record<string, unknown>;
}

export type HumanApprovalOutputJson<TInputJson extends Record<string, unknown>> = TInputJson & {
  readonly decision: HumanApprovalDecisionResult;
};

export interface DefinedHumanApprovalNode<
  TKey extends string,
  TConfig extends CredentialJsonRecord,
  TInputJson extends Record<string, unknown>,
  TBindings extends DefinedNodeCredentialBindings | undefined = undefined,
> extends DefinedNode<TKey, TConfig, TInputJson, HumanApprovalOutputJson<TInputJson>, TBindings> {
  readonly humanApprovalToolBehavior: { onRejected: "return" | "halt" };
}

export function isHumanApprovalNode(
  node: unknown,
): node is DefinedHumanApprovalNode<string, Record<string, unknown>, Record<string, unknown>, undefined> {
  return (
    typeof node === "object" &&
    node !== null &&
    "humanApprovalToolBehavior" in node &&
    typeof (node as { humanApprovalToolBehavior: unknown }).humanApprovalToolBehavior === "object"
  );
}

export function defineHumanApprovalNode<
  TKey extends string,
  TConfig extends CredentialJsonRecord,
  TInputJson extends Record<string, unknown>,
  TDecision extends Record<string, unknown>,
  TDelivery extends JsonValue,
  TBindings extends DefinedNodeCredentialBindings | undefined = undefined,
>(opts: {
  key: TKey;
  title: string;
  description?: string;
  icon?: string;
  channel: string;

  configSchema: ZodType<TConfig>;
  inputSchema?: ZodType<TInputJson>;
  decisionSchema: ZodType<TDecision>;
  credentials?: TBindings;

  approvedPredicate?: (decision: TDecision) => boolean;
  defaultTimeout?: Duration;
  defaultOnTimeout?: "halt" | "auto-accept";

  inspectorSummary?: (config: TConfig) => ReadonlyArray<NodeInspectorSummaryRow> | undefined;

  deliver: (
    args: {
      task: HumanTaskHandle;
      config: TConfig;
      input: TInputJson;
      item: Item;
    },
    ctx: ExecutionContext,
  ) => Promise<TDelivery>;

  onDecision?: (
    args: {
      decision: TDecision;
      actor: HumanTaskActor;
      task: HumanTaskHandle;
      delivery: TDelivery;
      item: Item;
    },
    ctx: ExecutionContext,
  ) => Promise<void>;

  onTimeout?: (
    args: {
      task: HumanTaskHandle;
      delivery: TDelivery;
      item: Item;
      policy: "halt" | "auto-accept";
    },
    ctx: ExecutionContext,
  ) => Promise<void>;
}): DefinedHumanApprovalNode<TKey, TConfig, TInputJson, TBindings> {
  const resolvedPredicate = resolveApprovedPredicate(opts.decisionSchema, opts.approvedPredicate);

  const timeout = opts.defaultTimeout ?? "24h";
  const onTimeout = opts.defaultOnTimeout ?? "halt";

  const inner = defineNode<TKey, TConfig, TInputJson, unknown, TBindings>({
    key: opts.key,
    title: opts.title,
    description: opts.description,
    icon: opts.icon,
    configSchema: opts.configSchema,
    inputSchema: opts.inputSchema,
    credentials: opts.credentials,
    inspectorSummary: opts.inspectorSummary ? ({ config }) => opts.inspectorSummary!(config) : undefined,

    async execute(args, { config, execution: ctx }) {
      if (!ctx.resumeContext) {
        const subject = buildSubject(opts.title, args.item, ctx);
        throw new SuspensionRequest({
          decisionSchema: opts.decisionSchema,
          timeout,
          onTimeout,
          subject,
          metadata: {
            channel: opts.channel,
            nodeKey: opts.key,
            approvedPredicateSource: opts.approvedPredicate?.toString() ?? null,
          },
          deliver: (handle: HumanTaskHandle) =>
            opts.deliver(
              {
                task: handle,
                config,
                input: args.input as TInputJson,
                item: args.item,
              },
              ctx,
            ),
        });
      }

      return await handleResume(
        args.item,
        ctx.resumeContext,
        opts.decisionSchema,
        resolvedPredicate,
        opts.onDecision,
        opts.onTimeout,
        ctx,
      );
    },
  });

  return Object.assign(inner, {
    humanApprovalToolBehavior: { onRejected: "return" as const },
  }) as unknown as DefinedHumanApprovalNode<TKey, TConfig, TInputJson, TBindings>;
}

function resolveApprovedPredicate<TDecision extends Record<string, unknown>>(
  schema: ZodType<TDecision>,
  predicate: ((d: TDecision) => boolean) | undefined,
): (d: TDecision) => boolean {
  if (predicate) {
    return predicate;
  }
  const shape = (schema as unknown as ZodObject<Record<string, ZodType>>).shape;
  if (shape && typeof shape === "object" && "approved" in shape) {
    return (d) => (d as { approved?: unknown }).approved === true;
  }
  throw new Error(
    `defineHumanApprovalNode: decisionSchema has no "approved" field and no approvedPredicate was provided. ` +
      `Either add { approved: z.boolean() } to the decision schema or supply approvedPredicate explicitly.`,
  );
}

function buildSubject(title: string, item: Item, ctx: NodeExecutionContext<NodeConfigBase>): HumanTaskSubject {
  return {
    title,
    summary: "",
    attributes: {
      workflowId: ctx.workflowId,
      nodeId: ctx.nodeId,
      item: item.json as JsonValue,
    },
  };
}

function mergeDecision(
  item: Item,
  decision: HumanApprovalDecisionResult,
): { json: Record<string, unknown>; binary: Item["binary"]; meta: Item["meta"] } {
  return {
    json: { ...(item.json as Record<string, unknown>), decision },
    binary: item.binary,
    meta: item.meta,
  };
}

async function handleResume<TDecision extends Record<string, unknown>, TDelivery extends JsonValue>(
  item: Item,
  resumeContext: ResumeContext,
  decisionSchema: ZodType<TDecision>,
  resolvedPredicate: (d: TDecision) => boolean,
  onDecision:
    | ((
        args: {
          decision: TDecision;
          actor: HumanTaskActor;
          task: HumanTaskHandle;
          delivery: TDelivery;
          item: Item;
        },
        ctx: ExecutionContext,
      ) => Promise<void>)
    | undefined,
  onTimeoutCb:
    | ((
        args: {
          task: HumanTaskHandle;
          delivery: TDelivery;
          item: Item;
          policy: "halt" | "auto-accept";
        },
        ctx: ExecutionContext,
      ) => Promise<void>)
    | undefined,
  ctx: ExecutionContext,
): Promise<{ json: Record<string, unknown>; binary: Item["binary"]; meta: Item["meta"] }> {
  const { decision: dec, delivery, task } = resumeContext;

  if (dec.kind === "timed_out" || dec.kind === "auto_accepted") {
    const policy: "halt" | "auto-accept" = dec.kind === "auto_accepted" ? "auto-accept" : "halt";
    await onTimeoutCb?.({ task, delivery: delivery as TDelivery, item, policy }, ctx);
    const status = dec.kind === "auto_accepted" ? "auto-accepted" : "timed-out";
    return mergeDecision(item, { status, decidedAt: dec.at });
  }

  const parsed = decisionSchema.parse(dec.value);
  await onDecision?.(
    {
      decision: parsed,
      actor: dec.actor,
      task,
      delivery: delivery as TDelivery,
      item,
    },
    ctx,
  );

  const isApproved = resolvedPredicate(parsed);
  return mergeDecision(item, {
    status: isApproved ? "approved" : "rejected",
    actor: dec.actor,
    decidedAt: dec.decidedAt,
    note: (parsed as { note?: string }).note,
    payload: parsed,
  });
}
